/**
 * White-label accent plumbing and WCAG contrast maths.
 *
 * Branding (Phase 3.8) swaps `--accent` alone; every accent-derived token
 * (`--a-ink`, `--a-soft`, `--a-bd`) follows via `color-mix`. The one thing that
 * does NOT follow automatically is the text drawn ON the accent — the mock
 * hard-codes `#fff`, which silently fails contrast under a pale brand colour.
 *
 * `applyAccent` fixes that: it sets `--accent` and recomputes `--on-accent` to
 * whichever of black/white clears AA against the chosen colour, and reports the
 * ratio so a branding UI can warn before saving. Nothing here fakes validation —
 * it is pure colour maths the design-reference §8 fix list calls for.
 */

/** Minimum contrast for normal text / for large text or UI, per WCAG 2.1 AA. */
export const AA_TEXT = 4.5;
export const AA_LARGE = 3;

export interface Rgb {
  r: number;
  g: number;
  b: number;
}

/** Parse `#rgb`/`#rrggbb` (with or without the hash) into 0–255 channels. */
export function parseHex(hex: string): Rgb | null {
  const cleaned = hex.trim().replace(/^#/, '');
  const full =
    cleaned.length === 3
      ? cleaned
          .split('')
          .map((c) => c + c)
          .join('')
      : cleaned;
  if (!/^[0-9a-fA-F]{6}$/.test(full)) return null;
  return {
    r: parseInt(full.slice(0, 2), 16),
    g: parseInt(full.slice(2, 4), 16),
    b: parseInt(full.slice(4, 6), 16),
  };
}

function channel(value: number): number {
  const c = value / 255;
  return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
}

/** Relative luminance (WCAG 2.1 formula), 0 (black) → 1 (white). */
export function relativeLuminance({ r, g, b }: Rgb): number {
  return 0.2126 * channel(r) + 0.7152 * channel(g) + 0.0722 * channel(b);
}

/** Contrast ratio between two colours, 1 → 21. Order-independent. */
export function contrastRatio(a: Rgb, b: Rgb): number {
  const la = relativeLuminance(a);
  const lb = relativeLuminance(b);
  const [hi, lo] = la >= lb ? [la, lb] : [lb, la];
  return (hi + 0.05) / (lo + 0.05);
}

const BLACK: Rgb = { r: 11, g: 15, b: 25 }; // matches --bg dark (#0b0f19)
const WHITE: Rgb = { r: 255, g: 255, b: 255 };

export interface AccentInk {
  /** The ink hex that reads best on the accent. */
  ink: string;
  /** Its contrast ratio against the accent. */
  ratio: number;
  /** Whether that ratio clears AA for normal-size text. */
  passesAA: boolean;
}

/**
 * Pick the readable ink (near-black or white) for text on `accentHex`, and
 * report whether it clears AA. Returns white for an unparseable colour so a bad
 * value degrades safely rather than throwing.
 */
export function readableInk(accentHex: string): AccentInk {
  const accent = parseHex(accentHex);
  if (accent === null) return { ink: '#ffffff', ratio: 1, passesAA: false };
  const onBlack = contrastRatio(accent, BLACK);
  const onWhite = contrastRatio(accent, WHITE);
  return onBlack >= onWhite
    ? { ink: '#0b0f19', ratio: onBlack, passesAA: onBlack >= AA_TEXT }
    : { ink: '#ffffff', ratio: onWhite, passesAA: onWhite >= AA_TEXT };
}

export interface ApplyAccentResult extends AccentInk {
  applied: boolean;
}

/**
 * Set `--accent` (and the recomputed `--on-accent`) on a root element — the
 * branding "apply" mechanism. Returns the chosen ink and its ratio so the caller
 * can surface a contrast warning; refuses an unparseable colour without touching
 * the DOM.
 */
export function applyAccent(
  accentHex: string,
  root: HTMLElement = document.body,
): ApplyAccentResult {
  const parsed = parseHex(accentHex);
  const ink = readableInk(accentHex);
  if (parsed === null) return { ...ink, applied: false };
  root.style.setProperty('--accent', accentHex);
  root.style.setProperty('--on-accent', ink.ink);
  return { ...ink, applied: true };
}

/** Remove any inline accent override, restoring the stylesheet default. */
export function clearAccent(root: HTMLElement = document.body): void {
  root.style.removeProperty('--accent');
  root.style.removeProperty('--on-accent');
}
